<script lang="ts" setup>
import { ArrowLeftIcon, TrashIcon } from '@heroicons/vue/20/solid';
import type { ChatConversation } from 'types';

defineProps<{
    conversations: ChatConversation[];
    activeId: string | null;
    isLoading: boolean;
}>();

const emit = defineEmits<{
    select: [id: string];
    delete: [id: string];
    back: [];
}>();
</script>

<template>
    <div class="flex flex-1 flex-col overflow-hidden">
        <div class="flex items-center gap-2 border-b border-neutral-200 px-4 py-2.5 dark:border-neutral-700">
            <button
                type="button"
                class="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
                aria-label="Back to chat"
                @click="emit('back')">
                <ArrowLeftIcon class="size-4" />
            </button>
            <span class="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Conversations</span>
        </div>

        <div class="flex-1 overflow-y-auto">
            <div
                v-if="isLoading"
                class="flex items-center justify-center py-8 text-sm text-neutral-400">
                Loading...
            </div>

            <div
                v-else-if="conversations.length === 0"
                class="flex items-center justify-center py-8 text-sm text-neutral-400 dark:text-neutral-500">
                No conversations yet
            </div>

            <div
                v-for="conversation in conversations"
                :key="conversation.id"
                role="button"
                tabindex="0"
                class="group flex w-full cursor-pointer items-center gap-2 border-b border-neutral-100 px-4 py-3 text-left transition-colors hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-800"
                :class="conversation.id === activeId ? 'bg-neutral-50 dark:bg-neutral-800' : ''"
                @click="emit('select', conversation.id)"
                @keydown.enter="emit('select', conversation.id)">
                <div class="min-w-0 flex-1">
                    <div class="truncate text-sm text-neutral-800 dark:text-neutral-200">
                        {{ conversation.title }}
                    </div>
                    <div class="mt-0.5 text-[11px] text-neutral-400 dark:text-neutral-500">
                        {{ new Date(conversation.updated_at).toLocaleDateString() }}
                    </div>
                </div>
                <button
                    type="button"
                    class="shrink-0 rounded p-1 text-neutral-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100 dark:text-neutral-500 dark:hover:text-red-400"
                    aria-label="Delete conversation"
                    @click.stop="emit('delete', conversation.id)">
                    <TrashIcon class="size-3.5" />
                </button>
            </div>
        </div>
    </div>
</template>
